import { useEffect, useRef, useState } from 'react';
import { Drawer, Head, SaveView, sortOf, useAdmin, useDrawerRoute, useFilters } from '../ui/Shell';
import { Block, Cell, DateRange, Grid, Kv, ListMore, Pill, Table, type Row, type Tone } from '../ui/Block';
import { fmtDate, post, type Env } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { countOf, qs, useDebounced, usePagedList, type Paged } from '../lib/list';

type Status = 'open' | 'waiting' | 'closed';
interface Ticket {
  id: string; number: number; subject: string; status: Status; createdAt: string; updatedAt: string;
  unread: boolean; messageCount: number; lastMessage: { author: 'customer' | 'staff'; preview: string; at: string } | null;
  email: string; name: string; source: 'app' | 'email' | 'contact_form'; principalId: string | null; principalName: string | null;
}
interface Message { id: string; author: 'customer' | 'staff'; authorName: string; text: string; via: 'app' | 'admin' | 'email'; createdAt: string; staffUser?: string | null; senderVerified?: boolean; attachments: { filename?: string; contentType?: string; size?: number }[] }
interface Detail { ticket: Ticket; messages: Message[] }

const STATUS: Record<Status, { label: string; tone: Tone }> = {
  open: { label: 'OPEN', tone: 'warn' },
  waiting: { label: 'WAITING ON CUSTOMER', tone: 'info' },
  closed: { label: 'CLOSED', tone: 'mute' },
};
const SOURCE: Record<Ticket['source'], string> = { app: 'App', email: 'Email', contact_form: 'Contact form' };
const who = (t: Ticket) => (t.name ? `${t.name} <${t.email}>` : t.email);

function useSearch(value: string, write: (v: string) => void): [string, (v: string) => void] {
  const [q, setQ] = useState(value);
  const dq = useDebounced(q.trim(), 300);
  useEffect(() => { if (dq !== value) write(dq); }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setQ((c) => (c.trim() === value ? c : value)); }, [value]);
  return [q, setQ];
}

function TicketDrawer({ env, id, paused, onChanged }: { env: Env; id: string; paused: boolean; onChanged: () => void }) {
  const ctx = useAdmin();
  const d = useData<Detail>(env, `/admin/support/tickets/${id}`, 20, paused);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const t = d.data?.ticket;
  useEffect(() => { post(env, `/admin/support/tickets/${id}/read`).then(onChanged).catch(() => {}); }, [env, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const message = draft.trim();
    if (!message || busy) return;
    setBusy('Sending…');
    try {
      await post(env, `/admin/support/tickets/${id}/messages`, { message });
      setDraft('');
      ctx.toast(`Replied to #${t?.number}`, `Emailed to ${t?.email}`);
      d.reload();
      onChanged();
    } catch (e) {
      ctx.toast('Reply not sent', e instanceof Error ? e.message : String(e), { logged: false });
    } finally {
      setBusy(null);
    }
  };
  const setStatus = async (status: Status) => {
    setBusy('Saving…');
    try {
      await post(env, `/admin/support/tickets/${id}/status`, { status });
      ctx.toast(`#${t?.number} ${status === 'closed' ? 'closed' : status === 'open' ? 'reopened' : 'marked waiting'}`);
      d.reload();
      onChanged();
    } catch (e) {
      ctx.toast('Status not changed', e instanceof Error ? e.message : String(e), { logged: false });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Drawer eyebrow={t ? `SUPPORT #${t.number}` : 'SUPPORT'} title={t?.subject ?? '…'} sub={t ? `${who(t)} · ${SOURCE[t.source]} · opened ${fmtDate(t.createdAt)}` : undefined}
      chip={t ? <Pill tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Pill> : undefined}
      acts={t && (
        t.status === 'closed'
          ? <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => setStatus('open')}>Reopen</button>
          : <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => setStatus('closed')}>Close</button>
      )} wide>
      {!t && d.loading && <div style={{ color: 'var(--ink-45)', fontSize: 12 }}>Loading…</div>}
      {describeErr(d.error) && <div style={{ color: 'var(--conf-low)', fontSize: 12 }}>{describeErr(d.error)!.title}</div>}
      {t && d.data && (
        <>
          <Kv rows={[
            { k: 'Customer', v: { t: who(t), sub: t.principalName ? `Account: ${t.principalName}` : 'No account', mono: false } },
            ...(t.principalId ? [{ k: 'Account id', v: { t: t.principalId, mono: true } }] : []),
            { k: 'Last activity', v: { t: fmtDate(t.updatedAt) } },
          ]} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            {d.data.messages.map((m) => {
              const staff = m.author === 'staff';
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: staff ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '88%', padding: '9px 12px', borderRadius: 10, background: staff ? 'var(--brand-10, var(--fill-08))' : 'var(--fill-05)', color: 'var(--ink-96)', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {m.text}
                    {m.attachments?.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--ink-62)' }}>
                        Attached (not kept): {m.attachments.map((a) => a.filename || 'file').join(', ')}
                      </div>
                    )}
                  </div>
                  <div style={{ font: '400 10.5px var(--vs-font-mono)', color: 'var(--ink-45)', marginTop: 3 }}>
                    {staff ? (m.staffUser || m.authorName || 'staff') : (m.authorName || t.email)} · via {m.via} · {fmtDate(m.createdAt)}
                    {m.senderVerified === false && <span style={{ color: 'var(--conf-low)' }}> · SENDER NOT VERIFIED (may be forged)</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <textarea className="field__input" aria-label="Reply" value={draft} rows={6} style={{ resize: 'vertical', fontSize: 13, lineHeight: 1.5 }}
              placeholder={`Reply to ${t.name || t.email}. It is emailed to them; their answer comes back here.`}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, fontSize: 11.5, color: 'var(--ink-45)' }}>⌘↵ sends. The ticket then waits on the customer.</span>
              <button type="button" className="btn btn--sm btn--primary" disabled={!draft.trim() || !!busy} onClick={send}>{busy ?? 'Send reply'}</button>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}

type Filter = Status | 'all';

export function Support() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [f, set] = useFilters({ status: 'open', q: '', from: '', to: '', sort: '', dir: '' });
  const s = sortOf(f, set, { key: 'attention', dir: 'desc' });
  const status = f.status as Filter;
  const [q, setQ] = useSearch(f.q, (v) => set({ q: v }));
  const path = `/admin/support/tickets${qs({ status, q: f.q, from: f.from, to: f.to, sort: f.sort, dir: f.sort ? s.sort.dir : '' })}`;
  const l = usePagedList<Ticket, Paged & { tickets: Ticket[]; counts: Record<Status, number> }>(env, path, 'tickets', 20, paused);
  const counts = l.data?.counts;
  const unread = l.rows.filter((t) => t.unread).length;

  const reload = useRef(l.reload);
  reload.current = l.reload;
  const changed = () => { reload.current(); ctx.reloadAttention(); };
  const drawer = (id: string) => <TicketDrawer env={env} id={id} paused={paused} onChanged={changed} />;
  const open = (id: string) => ctx.openDrawer(`support:${id}`, drawer(id));
  useDrawerRoute('support', drawer, []);
  useEffect(() => {
    const id = ctx.params.id;
    if (!id) return;
    open(id);
    const { id: _, ...rest } = ctx.params;
    ctx.setParams(rest);
  }, [ctx.params.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows: Row[] = l.rows.map((t) => ({
    key: t.id,
    onClick: () => open(t.id),
    cells: [
      { t: `#${t.number}`, mono: true, dot: t.unread ? 'warn' : undefined, hollow: !t.unread },
      { t: t.subject, fw: t.unread ? 600 : undefined, sub: t.lastMessage ? `${t.lastMessage.author === 'staff' ? 'You: ' : ''}${t.lastMessage.preview}` : undefined },
      { t: t.name || t.email, sub: t.name ? t.email : t.principalName ?? undefined },
      { t: SOURCE[t.source], sub: `${t.messageCount} message${t.messageCount === 1 ? '' : 's'}` },
      { pill: STATUS[t.status] },
      { t: fmtDate(t.lastMessage?.at ?? t.updatedAt), mono: true },
    ],
  }));

  const chip = (s: Filter, label: string) => ({ label: counts && s !== 'all' ? `${label} · ${counts[s].toLocaleString()}` : label, on: status === s, onClick: () => set({ status: s }) });

  return (
    <>
      <Head title="Support" sub="Requests from the app, the contact form and emails to support. Open a row to read and reply; replies are emailed, and the customer's answers come back here." />
      <Grid>
        <Cell span={12}>
          <Block title="Tickets" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 60} loading={l.loading && !l.data}
            error={describeErr(l.error) ? { ...describeErr(l.error)!, retry: l.reload } : null}
            search={{ value: q, placeholder: '#number, subject, email or a word in a message', onChange: setQ }}
            chips={[chip('open', 'Open'), chip('waiting', 'Waiting'), chip('closed', 'Closed'), chip('all', 'All')]}
            extra={<><DateRange label="Last message" from={f.from} to={f.to} onChange={(from, to) => set({ from, to })} /><SaveView /></>}
            empty={l.data && l.rows.length === 0 ? (status === 'open' && !f.q && !f.from && !f.to ? 'Nothing open. Inbox zero.' : 'No tickets match.') : undefined}
            emptyGood={status === 'open' && !f.q && !f.from && !f.to}
            foot={<ListMore list={l} noun="tickets" />}>
            {unread > 0 && <div className="blk__filters" style={{ borderTop: 0, fontSize: 12, color: 'var(--ink-62)' }}>{unread} loaded with a customer message you have not read.</div>}
            <Table mobile={mobile} minWidth={920} rows={rows} sort={s.sort} onSort={s.onSort}
              cols={[{ label: '#', width: '90px', sort: 'number' }, { label: 'SUBJECT', width: 'minmax(260px,2.4fr)', sort: 'subject', sortAsc: true }, { label: 'CUSTOMER', width: 'minmax(180px,1.2fr)', sort: 'customer', sortAsc: true }, { label: 'FROM', width: '120px', sort: 'source', sortAsc: true }, { label: 'STATUS', width: '190px', sort: 'status', sortAsc: true }, { label: 'LAST', width: '130px', sort: 'last' }]} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
